'use client';

import {useState, useEffect, useContext, useRef} from 'react';
import Box from '@mui/material/Box';
import { chartColors } from '@/styles/theme/colors';
import DataContext from '@/contexts/DataContext';
import { fetchExprData, fetchTooltip, extractTrackedParams, extractChangedParams } from '@/lib/dataHandlers/DataFetchers';
import { getDefaultColor } from '@/lib/dataHandlers/ChartHelpers';
import { useComponentLogger } from '@/lib/component-logger';

import { InfoTooltip } from '../typography/info-tooltip';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Cell, Pie, PieChart, Tooltip } from 'recharts';

import { NoSsr } from '@/components/core/no-ssr';

export function Donut({ config }) {
    const logger = useComponentLogger("Donut");
    // donutData should be in the form:
    // [{name: 'name', value: 50.5, color:'var(--mui-palette-primary-main)'}, ...]
    const { currentProject, currentView, selectedViewParams, changedParams } = useContext(DataContext);
    const [tooltip, setTooltip] = useState(null);
    const [donutData, setDonutData] = useState([]);
    const [donutSummary, setDonutSummary] = useState(null);
    const [title, setTitle] = useState(config.title || '');
    const chartSize = 200;
    const chartThickness = 30;
    const directionSM = config.size && config.size === 100 ? 'row' : 'column';
    const donutWidthSM = config.size && config.size === 100 ? '40%' : '100%';
    const trackedParamValsRef = useRef({});
    const trackedParamNamesRef = useRef({}); // names of the params that this component depends on, extracted from its api expr

    // logger.debug('donut object', donutObject);

    // fetch and set content
    const fetchContent = async () => {
        if (config?.dataSource === null || config?.dataSource === undefined) return;

        const dataSource = config.dataSource;
        
        if (dataSource.type === "text") {
            setDonutData(config.dataSource.text);
            // logger.debug('donut content', donutObject.dataSource.text);
        } else if (dataSource.type === "apiExpr") {
            const data = await fetchExprData(currentProject, selectedViewParams, dataSource.expr);
            logger.debug('donut data', data);

            // rows is an array of 1 obj, with a key per data point
            // cols is an array of objects, 1 object epr data point
            if (data?.rows && data.rows.length > 0 && data?.cols && data.cols.length > 0) {
                let chartDataArr = [];
                let donutRows = data.rows[0];
                let donutCols = data.cols;
                let colorIndex = 0;
                let colorsUsed = [];
                for (const col of donutCols) {
                    if (col.name === 'ts') continue;
                    let donutObject = {};
                    // grab the title and color from the cols
                    let colMeta = col.meta;
                    
                    let title = colMeta.dis || colMeta.navName || colMeta.name || '';

                    let color;
                    if (colMeta.color) {
                        color = chartColors[colMeta.color] || colMeta.color;
                    } else {
                        let colorData = getDefaultColor(colorIndex);
                        color = colorData.hex;
                        colorIndex++;
                    }
                    
                    colorsUsed.push(color);
                    
                    let item = donutRows[col.name];
                    if (item === null || item === undefined) continue;
                    if (item.val === null || item.val === undefined) continue;
                    chartDataArr.push({
                        name: title,
                        value: Math.round(item.val * 100) / 100, // round to 2 decimal placesitem.val,
                        unit: item.unit || '',
                        color: color
                    });
                    
                }

                // logger.debug('donut data end', chartDataArr);
                setDonutData(chartDataArr);
                
                // if (config.title) setTitle(config.title);
            }

            if (data.meta && data.meta.statName && data.meta.statVal) {
                // logger.debug('statval:', data.meta.statName, data.meta.statVal);
                setDonutSummary({
                    name: data.meta.statName,
                    value: data.meta.statVal.val,
                    unit: data.meta.statVal.unit || ''
                });
            }

        }
    }

    const processTooltip = async() => {
        let tooltipContent = await fetchTooltip(currentProject, selectedViewParams, config);
        // logger.debug('donut tooltip', tooltipContent);
        if (tooltipContent) setTooltip(tooltipContent);
    }

    useEffect(() => {
        let trackedParams = extractTrackedParams(config.dataSource);
        let params = {};
        for (const param of trackedParams) {
            if (selectedViewParams[param]) params[param] = selectedViewParams[param].value;
        }

        logger.debug('tracked params', params, trackedParams);
        trackedParamValsRef.current = params;
        trackedParamNamesRef.current = trackedParams;
        
        fetchContent().catch(() => {});
        processTooltip().catch(() => {});
    }, []);

    useEffect(() => {
        let shouldReFetch = extractChangedParams(trackedParamNamesRef.current, changedParams, selectedViewParams, trackedParamValsRef.current);
        logger.debug('changed params', changedParams, trackedParamNamesRef.current, shouldReFetch, selectedViewParams);
        if (shouldReFetch.changed) {
            trackedParamValsRef.current = shouldReFetch.paramRef;
            fetchContent();
            processTooltip();
        }

        // make sure to clear the content if any of the tracked params were removed
        // common use case: when a parent param changes, it clears the child param
        for (const param of trackedParamNamesRef?.current) { 
            if (!selectedViewParams[param]) {
                setDonutData([]);
                return;
            }
        }
    }, [changedParams]);

    return (
    <>
    {title ? 
        <Typography color="text.secondary" variant="h6" marginBottom={5} sx={{display: 'flex', alignItems: 'center'}}>
            {config?.title}
            {tooltip ? <InfoTooltip content={tooltip} /> : <></>}
        </Typography>
    : <></>}
    {/* <Stack divider={<Divider />} spacing={3}> */}
    <Stack spacing={3} direction={{ xs: 'column', sm: directionSM }}>
        <Box width={{xs: '100%', sm: donutWidthSM}} sx={{ display: 'flex', justifyContent: 'center', }}>
        <NoSsr fallback={<Box sx={{ height: `${chartSize}px`, width: `${chartSize}px` }}  />}>
            <PieChart height={chartSize} margin={{ top: 0, right: 0, bottom: 0, left: 0 }} width={chartSize} style={{alignItems: {sm: 'center'}}}>
            <Pie
                animationDuration={300}
                cx={chartSize / 2}
                cy={chartSize / 2}
                data={donutData}
                dataKey="value"
                innerRadius={chartSize / 2 - chartThickness}
                nameKey="name"
                outerRadius={chartSize / 2}
                strokeWidth={0}
            >
                {donutData?.map((entry) => (
                <Cell fill={entry.color} key={entry.name} />
                ))}
            </Pie>
            <Tooltip animationDuration={50} content={<TooltipContent />} />
            </PieChart>
        </NoSsr>
        </Box>
        <Stack spacing={3} sx={{ flex: '1 1 auto' }}>
            <Summary payload={donutSummary}/>
            <Legend payload={donutData} directionSM={directionSM} />
        </Stack>
    </Stack>
    </>
    );
}

function Summary({payload}) {
    if (payload === null || payload === undefined) return null;
    if (!payload.value) return null;
    // logger.debug('is decimal', payload.value % 1);
    return (
        <Stack spacing={1}>
            <Typography color="text.secondary" variant="overline">
                {payload.name || ''}
            </Typography>
            <Typography variant="h4">
                {payload.value % 1 > 0 ? `${payload.value.toFixed(2)} ${payload.unit}` : `${payload.value} ${payload.unit}`}
            </Typography>
        </Stack>
    );
}

function Legend({payload, directionSM}) {
    let widthSM = directionSM === 'row' ? '90%' : '100%';
    let widthMD = directionSM === 'row' ? '75%' : '100%';
    return (
        <Stack component="ul" spacing={2} sx={{ listStyle: 'none', m: 0, p: 0 }}>
            {payload?.map((entry) => (
                <Stack component="li" direction="row" key={entry.name} spacing={1} sx={{ alignItems: 'center' }} width={{xs: '100%',sm: widthSM, md: widthMD}}>
                    {/* <Box sx={{ bgcolor: entry.color, borderRadius: '2px', height: '4px', width: '16px' }} /> */}
                    <Box sx={{ bgcolor: entry.color, borderRadius: '4px', height: '8px', width: '18px' }} />
                    <Typography sx={{ flex: '1 1 auto' }} variant="subtitle2" width={{xs: '66%', sm: '75%'}}>
                        {entry.name}
                    </Typography>
                    <Typography color="text.secondary" width={{xs: '33%', sm: '25%'}}>
                        {`${entry.value.toFixed(2)} ${entry.unit}`}
                    </Typography>
                </Stack>
            ))}
        </Stack>
    )

}

function LegendBig({ payload }) {
    // logger.debug('payload:', payload);
    // determine #of columns
    let desktopCol = payload.length > 4 ? 'auto auto auto' : 'auto auto';
    
    return (
        <Box width={{xs: '100%', sm: '60%'}} sx={{ display: 'grid', gap: 3, gridTemplateColumns: {xs: 'repeat(auto-fit, minmax(100px, 1fr))', sm: 'desktopCol'}}}>
        {/* <Box width={{xs: '100%', sm: '60%'}} sx={{ display: 'grid', gap: 3, gridTemplateColumns: {xs: 'auto', sm: 'desktopCol'}}}> */}
        {payload?.map((entry) => (
            // <Box key={entry.name} display="inline-grid" justifyItems="center">
            <Box key={entry.name}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Box sx={{ bgcolor: entry.color, borderRadius: '2px', height: '4px', width: '16px' }} />
                <Typography variant="body2">{entry.name}</Typography>
            </Stack>
            <Typography variant="h5">
                {/* {new Intl.NumberFormat('en-US', { style: 'percent', maximumFractionDigits: 2 }).format(entry.value / 100)} */}
                {`${entry.value.toFixed(2)} ${entry.unit}`}
            </Typography>
            </Box>
        ))}
        </Box>
    );
}

function TooltipContent({ active, payload }) {
  if (!active) {
    return null;
  }

  return (
    <Paper sx={{ border: '1px solid var(--mui-palette-divider)', boxShadow: 'var(--mui-shadows-16)', p: 1 }}>
      <Stack spacing={2}>
        {payload?.map((entry) => (
          <Stack direction="row" key={entry.name} spacing={3} sx={{ alignItems: 'center' }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flex: '1 1 auto' }}>
              <Box sx={{ bgcolor: entry.payload?.fill, borderRadius: '2px', height: '8px', width: '8px' }} />
              <Typography sx={{ whiteSpace: 'nowrap' }}>{entry.name}</Typography>
            </Stack>
            <Typography color="text.secondary" variant="body2">
              {new Intl.NumberFormat('en-US', { style: 'percent', maximumFractionDigits: 2 }).format(entry.value / 100)}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
}
